/**
 * Filter system utilities for EventSearch
 * Provides data model, factory functions, and query building logic
 */

// Filter types
export const FILTER_TYPES = {
  FIELD_VALUE: 'field-value',   // field:value (e.g., ip_src:1.1.1.1)
  VALUE_ONLY: 'value-only',     // just value in any field
  TIME_RANGE: 'time-range',     // ts:[start TO end]
  COMPOUND: 'compound'          // multiple field:value pairs in one filter
}

// Filter operators (how multiple values are combined)
export const FILTER_OPERATORS = {
  ANYOF: 'anyof',  // OR - match any of the values
  ALL: 'all',      // AND - match all values
  NOT: 'not'       // NOT - exclude all values
}

// Queue item modes (during ctrl-click selection)
export const QUEUE_MODES = {
  FIELD_VALUE: 'field-value',   // 1 click: filter by field:value
  VALUE_ONLY: 'value-only'      // 2 clicks: filter by value in any field
}

/**
 * Generate a unique ID for filters and queue items
 */
export function generateFilterId() {
  return `filter-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

/**
 * Create a new filter object
 * @param {string} type - FILTER_TYPES value
 * @param {string|null} field - field name (null for value-only or time)
 * @param {Array|Object} values - array of values or {start, end} for time
 * @param {string|null} operator - FILTER_OPERATORS value (null for time)
 * @returns {Object} filter object
 */
export function createFilter(type, field, values, operator) {
  return {
    id: generateFilterId(),
    type,
    field,
    values,
    operator,
    enabled: true,
    createdAt: Date.now()
  }
}

/**
 * Create a time range filter
 * @param {number} startMs - start time in milliseconds
 * @param {number|null} endMs - end time in milliseconds (null for open-ended)
 * @returns {Object} time filter object
 */
export function createTimeFilter(startMs, endMs = null) {
  return createFilter(
    FILTER_TYPES.TIME_RANGE,
    null,
    { start: startMs, end: endMs },
    null
  )
}

/**
 * Format a value for use in a Lucene query
 * Always quotes values to ensure exact matching
 * @param {any} value - value to format
 * @returns {string} formatted value
 */
function formatQueryValue(value) {
  if (value === null || value === undefined) {
    return '*'
  }

  const strValue = String(value)
  // Always quote to ensure exact matching
  return `"${strValue.replace(/"/g, '\\"')}"`
}

/**
 * Convert a single filter to Lucene query syntax
 * @param {Object} filter - filter object
 * @returns {string} Lucene query string
 */
export function filterToQueryString(filter) {
  if (!filter.enabled) {
    return ''
  }

  switch (filter.type) {
    case FILTER_TYPES.TIME_RANGE: {
      const { start, end } = filter.values
      if (end === null || end === undefined) {
        // Open-ended time filter (no end bound)
        return `ts:[${start} TO *]`
      }
      return `ts:[${start} TO ${end}]`
    }

    case FILTER_TYPES.FIELD_VALUE: {
      const values = Array.isArray(filter.values) ? filter.values : [filter.values]
      const field = filter.field

      if (values.length === 0) return ''

      // Build value expressions
      const valueExprs = values.map(v => `${field}:${formatQueryValue(v)}`)

      let expr
      if (values.length === 1) {
        expr = valueExprs[0]
      } else {
        const joiner = filter.operator === FILTER_OPERATORS.ALL ? ' AND ' : ' OR '
        expr = `(${valueExprs.join(joiner)})`
      }

      if (filter.operator === FILTER_OPERATORS.NOT) {
        return `NOT ${expr}`
      }
      return expr
    }

    case FILTER_TYPES.VALUE_ONLY: {
      const values = Array.isArray(filter.values) ? filter.values : [filter.values]

      if (values.length === 0) return ''

      // Build value expressions (no field prefix)
      const valueExprs = values.map(v => formatQueryValue(v))

      let expr
      if (values.length === 1) {
        expr = valueExprs[0]
      } else {
        const joiner = filter.operator === FILTER_OPERATORS.ALL ? ' AND ' : ' OR '
        expr = `(${valueExprs.join(joiner)})`
      }

      if (filter.operator === FILTER_OPERATORS.NOT) {
        return `NOT ${expr}`
      }
      return expr
    }

    case FILTER_TYPES.COMPOUND: {
      const pairs = filter.values // Array of { field, value }
      if (!pairs || pairs.length === 0) return ''

      // Build expressions for each field:value pair
      const exprs = pairs.map(pair => {
        const formattedValue = formatQueryValue(pair.value)
        return pair.field
          ? `${pair.field}:${formattedValue}`
          : formattedValue
      })

      let expr
      if (exprs.length === 1) {
        expr = exprs[0]
      } else {
        const joiner = filter.operator === FILTER_OPERATORS.ALL ? ' AND ' : ' OR '
        expr = `(${exprs.join(joiner)})`
      }

      if (filter.operator === FILTER_OPERATORS.NOT) {
        return `NOT ${expr}`
      }
      return expr
    }

    default:
      return ''
  }
}

/**
 * Combine all enabled filters into a single query string
 * @param {Array} filters - array of filter objects
 * @returns {string} combined Lucene query string
 */
export function filtersToQueryString(filters) {
  const parts = filters
    .filter(f => f.enabled)
    .map(f => filterToQueryString(f))
    .filter(s => s.length > 0)

  if (parts.length === 0) {
    return ''
  }

  if (parts.length === 1) {
    return parts[0]
  }

  // Combine with AND (each filter is a separate constraint)
  return parts.map(p => `(${p})`).join(' AND ')
}

/**
 * Build the final query combining base query and filters
 * @param {string} baseQuery - user's base query
 * @param {Array} filters - array of filter objects
 * @returns {string} final query string
 */
export function buildFinalQuery(baseQuery, filters) {
  const filterQuery = filtersToQueryString(filters)
  const trimmedBase = baseQuery.trim()

  if (!trimmedBase && !filterQuery) {
    return ''
  }

  if (!trimmedBase) {
    return filterQuery
  }

  if (!filterQuery) {
    return trimmedBase
  }

  return `(${trimmedBase}) AND ${filterQuery}`
}

/**
 * Find an existing time filter in the filters array
 * @param {Array} filters - array of filter objects
 * @returns {Object|null} time filter or null
 */
export function findTimeFilter(filters) {
  return filters.find(f => f.type === FILTER_TYPES.TIME_RANGE) || null
}

/**
 * Group queue items by field for filter creation
 * @param {Array} queueItems - array of queue items
 * @returns {Object} { fieldValue: { field -> items }, valueOnly: items }
 */
export function groupQueueItems(queueItems) {
  const fieldValue = {}
  const valueOnly = []

  for (const item of queueItems) {
    if (item.mode === QUEUE_MODES.FIELD_VALUE) {
      if (!fieldValue[item.field]) {
        fieldValue[item.field] = []
      }
      fieldValue[item.field].push(item)
    } else {
      valueOnly.push(item)
    }
  }

  return { fieldValue, valueOnly }
}

/**
 * Create a single compound filter from grouped queue items
 * All selections are combined into one filter with multiple field:value pairs
 * @param {Object} grouped - result from groupQueueItems
 * @param {string} operator - FILTER_OPERATORS value
 * @returns {Array} array containing single compound filter
 */
export function createFiltersFromQueue(grouped, operator) {
  // Collect all field-value pairs into a single array
  const pairs = []

  // Add field-value pairs
  for (const [field, items] of Object.entries(grouped.fieldValue)) {
    items.forEach(item => {
      pairs.push({ field, value: item.rawValue })
    })
  }

  // Add value-only pairs (field is null)
  grouped.valueOnly.forEach(item => {
    pairs.push({ field: null, value: item.rawValue })
  })

  if (pairs.length === 0) return []

  // Create single compound filter containing all pairs
  return [createFilter(
    FILTER_TYPES.COMPOUND,
    null,
    pairs,  // Array of { field, value } objects
    operator
  )]
}
